import { DefaultModalName, IModal, IModalContext, ModalChildComponentContextKey, ModalContext } from '@AppModels/.';
import { UnregisterCallback } from 'history';
import { makeObservable, observable } from 'mobx';
import { observer, Provider } from 'mobx-react';
import * as React from 'react';
import ReactModal from 'react-modal';
import { IOperationResultNoValue } from '../../../extViewmodels';
import { AppHashHistory } from '../../constants/hash-history';
import { AppAutoUpdaterRefreshBlocker } from '../AppAutoUpdaterRefreshBlocker';
import { CloseButton } from '../CloseButton';
import './styles.less';

let appElement: string | HTMLElement;

export interface IModalProps<TResult = any> {
	className?: string;
	contentPresentationStyle?: IModalPresentationStyle;
	/* Function that will be called to get the parent element that the modal will be attached to. */
	getParentSelector?(): HTMLElement;
	headerAccessory?: React.ReactElement<any>;
	isOpen: boolean;
	name?: string;
	onAfterClose?(): void;
	onRequestClose(result?: TResult, canceled?: boolean, error?: IOperationResultNoValue): void;
	overlayPresentationStyle?: IModalPresentationStyle;
	shouldCloseOnOverlayClick?: boolean;
	useDefaultHeader?: boolean;
	useStyledHeader?: boolean;
}

interface IState {
	isActive?: boolean;
	isOpen?: boolean;
}

export interface IModalPresentationStyle {
	/** Will override &-after-open that is added by default */
	afterOpenClassName?: string;
	/** Will override &-before-close that is added by default */
	beforeCloseClassName?: string;
	/**
	 * Base class name. &-after-open and &-before-close will be automatically added for control over transitions unless
	 * afterOpenClassName and/or beforeCloseClassName are provided.
	 */
	className: string;
	/** Times in ms */
	transitionInDuration: number;

	/** Times in ms */
	transitionOutDuration: number;
}

export const DefaultOverlayPresentationStyle: IModalPresentationStyle = {
	className: 'modal-overlay',
	transitionInDuration: 450,
	transitionOutDuration: 450,
};

export const DefaultContentPresentationStyle: IModalPresentationStyle = {
	className: 'modal-overlay-content',
	transitionInDuration: 450,
	transitionOutDuration: 450,
};

// TODO: created a manager component that manages the display of multiple modals.
// create showAsModal and showModal functions that display in singleton modal manager component.
class _Modal<TResult> extends React.Component<React.PropsWithChildren<IModalProps<TResult>>, IState> {
	private mHistoryChangedUnregisterCallback: UnregisterCallback;
	public static defaultProps: Partial<IModalProps> = {
		isOpen: false,
	};
	public state: IState = {};
	/**
	 * Per React docs, we should track this as a separate var Use this with isOpen to first mount the modal and then open
	 * it (preserves presentation animations even when the modal is added to the dom in the open state).
	 */
	private didMount: boolean;

	private mChildContextParentModalProps: IModal;
	private mChildContext: IModalContext;

	static getDerivedStateFromProps<TResult = any>(nextProps: IModalProps<TResult>, state: IState) {
		const nextState: IState = {};
		if (nextProps.isOpen !== state.isOpen) {
			nextState.isOpen = nextProps.isOpen;
			if (nextState.isOpen) {
				nextState.isActive = true;
			}
		}
		return Object.keys(nextState).length > 0 ? nextState : null;
	}

	public constructor(props: IModalProps<TResult>) {
		super(props);

		makeObservable<_Modal<TResult>, 'didMount'>(this, {
			didMount: observable,
		});

		if (!appElement && import.meta.env.MODE !== 'test') {
			appElement = '#react-root';
			ReactModal.setAppElement(appElement);
		}

		this.mChildContextParentModalProps = {
			name: props.name || DefaultModalName,
			onRequestClose: this.onRequestClose,
		};
		this.mChildContext = { [ModalChildComponentContextKey]: this.mChildContextParentModalProps };
	}

	public componentDidMount() {
		const { isOpen } = this.props;
		this.didMount = true;
		this.setState({
			isActive: isOpen,
			isOpen,
		});

		// listen for history changes
		// do not use withRouter hoc to get history... you could get FullScreenModal history instead and that would cause onRequestClose to fire unexpectedly
		this.mHistoryChangedUnregisterCallback = AppHashHistory.listen(this.onHistoryChanged);
	}

	public componentWillUnmount() {
		this.unRegisterHistoryChangedListener();
	}

	public render() {
		const { headerAccessory, children, useStyledHeader } = this.props;
		const { isActive, isOpen } = this.state;
		const useDefaultHeader = !!this.props.useDefaultHeader && !headerAccessory && !useStyledHeader;
		return (
			<ReactModal
				bodyOpenClassName={null}
				className={this.getModalClassNames()}
				closeTimeoutMS={this.closeTimeoutMS}
				isOpen={this.didMount && isOpen}
				onAfterClose={this.onAfterClose}
				onRequestClose={this.onClickOutside}
				overlayClassName={this.getOverlayClassNames()}
				parentSelector={this.getParentSelector}
				portalClassName={`modal ${isActive ? 'modal-active' : ''} ${this.props.className || ''}`}
				shouldCloseOnOverlayClick={this.props.shouldCloseOnOverlayClick}
			>
				<React.Fragment key='react-modal-content'>
					{!!useDefaultHeader && (
						<div className={`${this.contentPresentationStyle.className}-header`}>
							<CloseButton onClick={this.onClose} />
						</div>
					)}
					{useStyledHeader && !useDefaultHeader ? (
						<div className='modal-overlay-content-header'>
							<CloseButton onClick={this.onClose} />
						</div>
					) : null}
					<Provider {...this.mChildContext}>
						<ModalContext.Provider value={this.mChildContext}>
							{headerAccessory}
							{children}
						</ModalContext.Provider>
					</Provider>
					<AppAutoUpdaterRefreshBlocker />
				</React.Fragment>
			</ReactModal>
		);
	}

	private onAfterClose = () => {
		this.setState({
			isActive: false,
			isOpen: false,
		});
		this.props.onAfterClose?.();
	};

	private getModalClassNames = () => {
		return {
			afterOpen:
				this.contentPresentationStyle.afterOpenClassName ?? `${this.contentPresentationStyle.className}-after-open`,
			base: `${this.contentPresentationStyle.className}`,
			beforeClose:
				this.contentPresentationStyle.beforeCloseClassName ?? `${this.contentPresentationStyle.className}-before-close`,
		};
	};

	private getOverlayClassNames = () => {
		return {
			afterOpen:
				this.overlayPresentationStyle.afterOpenClassName ?? `${this.overlayPresentationStyle.className}-after-open`,
			base: `${this.overlayPresentationStyle.className}`,
			beforeClose:
				this.overlayPresentationStyle.beforeCloseClassName ?? `${this.overlayPresentationStyle.className}-before-close`,
		};
	};

	private unRegisterHistoryChangedListener = () => {
		if (this.mHistoryChangedUnregisterCallback) {
			this.mHistoryChangedUnregisterCallback();
			this.mHistoryChangedUnregisterCallback = null;
		}
	};

	private onHistoryChanged = () => {
		if (this.state.isOpen) {
			this.onRequestClose(null, true);
		}
	};

	private getParentSelector = () => {
		const { getParentSelector } = this.props;
		const element: HTMLElement = getParentSelector ? getParentSelector() : null;
		return element || document.body;
	};

	private get overlayPresentationStyle() {
		return this.props.overlayPresentationStyle || DefaultOverlayPresentationStyle;
	}

	private get contentPresentationStyle() {
		return this.props.contentPresentationStyle || DefaultContentPresentationStyle;
	}

	private get closeTimeoutMS() {
		return Math.max(
			this.contentPresentationStyle.transitionOutDuration,
			this.overlayPresentationStyle.transitionOutDuration
		);
	}

	private onClickOutside = () => {
		this.onRequestClose(null, true);
	};

	private onClose = () => {
		this.onRequestClose(null, true);
	};

	private onRequestClose = (result?: TResult, canceled = false, error?: IOperationResultNoValue) => {
		if (this.props.onRequestClose) {
			// remove history listener
			this.unRegisterHistoryChangedListener();

			this.props.onRequestClose(result, canceled, error);
		}
	};
}

export const Modal = observer(_Modal as unknown as React.FC<any>);

export const setAppElement = (element: string | HTMLElement) => {
	appElement = element;
	ReactModal.setAppElement(appElement);
};

export function asModalComponent<T = any>(
	WrappedComponent: React.ComponentType<T>,
	defaultModalProps?: Partial<IModalProps>
) {
	// @ts-ignore
	const sfc: React.FunctionComponent<T & { modalProps?: IModalProps }> = (props: T & { modalProps: IModalProps }) => {
		const { modalProps, ...restProps } = props as any;
		const computedModalProps: IModalProps = {
			...(defaultModalProps || {}),
			...(modalProps || {}),
		};

		computedModalProps.className =
			!!defaultModalProps && !!defaultModalProps.className
				? `${defaultModalProps.className} ${(modalProps ? modalProps.className : undefined) || ''}`
				: computedModalProps.className;
		return (
			<Modal {...computedModalProps}>
				<WrappedComponent {...restProps} />
			</Modal>
		);
	};
	return sfc;
}
